import { useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import moment, { Moment } from "moment";

import { colors, InsertInvitationIcon } from "@dotnet/telematics-design-system";

import DatePicker from "components/DatePicker";
import ClearButton from "components/DatePicker/actionComponents/ClearButton";
import InputField, {
    INPUT_FIELD_SIZES
} from "components/DesignComponents/InputField";
import { useDateTime } from "shared";

import { DatePickerRangeProps } from "./types";

const DatePickerRange = ({
    onStartDateSelect,
    onEndDateSelect,
    startDateValue,
    endDateValue,
    isRequired,
    inputFieldCustomStyle,
    pickerCustomStyle,
    ...props
}: DatePickerRangeProps) => {
    const { textPlaceholder } = colors;

    const { t } = useTranslation();
    const { formattedDate } = useDateTime();

    const [isOpen, setIsOpen] = useState(false);

    const [startDate, setStartDate] = useState<Moment | null>(
        startDateValue || null
    );
    const [endDate, setEndDate] = useState<Moment | null>(endDateValue || null);

    const anchorRef = useRef(null);

    const setStartClearEnd = (newValue: Moment) => {
        setStartDate(newValue);
        onStartDateSelect(newValue);

        setEndDate(null);
        onEndDateSelect(null);
    };

    const handleSelect = (newValue: Moment | null) => {
        if (!newValue) return;

        if (!startDate) {
            setStartClearEnd(newValue);
            return;
        }

        if (!endDate) {
            if (
                newValue.isSame(startDate, "day") ||
                newValue.isAfter(startDate, "day")
            ) {
                setEndDate(newValue);
                onEndDateSelect(newValue);
                setIsOpen(false);
            } else {
                setStartClearEnd(newValue);
            }

            return;
        }

        setStartClearEnd(newValue);
    };

    const getPlaceholder = () => {
        const placeholder = moment.localeData().longDateFormat("L");
        return `${placeholder} ${t("Dialog##date to")} ${placeholder}`;
    };

    const getValue = () => {
        if (startDate && !endDate) {
            return `${formattedDate(startDate.toISOString(), "L")} ${t("Dialog##date to")}`;
        }

        if (startDate && endDate) {
            return `${formattedDate(startDate.toISOString(), "L")} ${t("Dialog##date to")} ${formattedDate(endDate.toISOString(), "L")}`;
        }

        return "";
    };

    const isReadOnly = props.readOnly;

    return (
        <div>
            <InputField
                labelLeft={t("Dialog##date range")}
                placeholder={getPlaceholder()}
                value={getValue()}
                fullWidth
                required={isRequired}
                ref={anchorRef}
                iconLeft={<InsertInvitationIcon />}
                iconRight={
                    startDate && !isReadOnly ? (
                        <ClearButton
                            onClick={(e: { stopPropagation: () => void }) => {
                                e.stopPropagation();
                                setStartDate(null);
                                setEndDate(null);
                                onStartDateSelect(null);
                                onEndDateSelect(null);
                            }}
                        />
                    ) : null
                }
                size={INPUT_FIELD_SIZES.Medium}
                readOnly={isReadOnly}
                onClick={() => !isReadOnly && setIsOpen(true)}
                customStyle={{
                    ...inputFieldCustomStyle,

                    "& .MuiInputAdornment-positionStart": {
                        svg: {
                            color: textPlaceholder
                        }
                    }
                }}
            />

            <DatePicker
                {...props}
                customAnchorEl={anchorRef.current}
                open={isOpen}
                value={startDate}
                closeOnSelect={false}
                rangePickerValues={{
                    startDate,
                    endDate
                }}
                onClose={() => setIsOpen(false)}
                onRangeDateSelect={value => handleSelect(value)}
                customStyle={pickerCustomStyle}
            />
        </div>
    );
};

export default DatePickerRange;
